'use client';

import { Box, Button, Tooltip } from '@mui/material';
import {
  DataGridPro,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
  type GridColDef,
  type GridSortModel,
  type GridColumnVisibilityModel,
} from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { PublicationDto, ScienceDriverDto } from '@/api/ApiClient';
import { useBackend } from '@/hooks/useBackend';
import {
  usePublicationFilterStore,
  loadStoredPublicationColumnVisibility,
  COLUMN_VISIBILITY_STORAGE_KEY,
} from '@/stores/publicationFilterStore';
import { scopedLocalStorage } from '@/lib/browser/scopedStorage';
import { dataGridStyle } from '@/styles/tableStyles';
import { getPublicationTypeDisplay } from '@/lib/types/publicationTypes';
import { removeLeadingTrailingBrTags } from '@/lib/utils/textUtils';
import { usePrivileges } from '@/hooks/usePrivileges';
import { PrivilegeEnum } from '@/api/ApiClient';
import { useDialogStore } from '@/stores/dialogStore';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useGridSearchErrorHandler } from '@/hooks/useGridSearchErrorHandler';
import { consumeNextSearchUserInitiated } from '@/stores/searchUiStore';
import { log } from '@/lib/utils/logger';
import { CreatePublicationDialog } from '@/components/dialog/CreatePublicationDialog';
import { stripDoiPrefix, toDoiHref } from '@/lib/utils/doiLinks';

const PUBLICATION_SORT_MODEL_STORAGE_KEY = 'publicationGridSortModel';

function loadStoredSortModel(): GridSortModel {
  try {
    const stored = scopedLocalStorage.getItem(PUBLICATION_SORT_MODEL_STORAGE_KEY);
    if (!stored) return [{ field: 'year', sort: 'desc' }];
    const parsed = JSON.parse(stored);
    if (
      Array.isArray(parsed) &&
      parsed.every(
        (item) =>
          typeof item === 'object' && typeof item.field === 'string' && (item.sort === 'asc' || item.sort === 'desc')
      )
    ) {
      return parsed;
    }
  } catch {
    // ignore bad storage
  }
  return [{ field: 'year', sort: 'desc' }];
}

type PublicationRow = PublicationDto & { id: number };

export function PublicationGrid({ refreshNonce = 0 }: { refreshNonce?: number }) {
  const backend = useBackend();
  const { showConfirmDialog } = useDialogStore();
  const { showSnackbar } = useSnackbar();
  const { handleGridSearchError } = useGridSearchErrorHandler('publications');
  const { isAuthorized } = usePrivileges();
  const canEdit = isAuthorized(PrivilegeEnum.Publication, 'update');
  const canDelete = isAuthorized(PrivilegeEnum.Publication, 'delete');

  const filters = usePublicationFilterStore((state) => state.filters);
  const paginationModel = usePublicationFilterStore((state) => state.paginationModel);
  const setPaginationModel = usePublicationFilterStore((state) => state.setPaginationModel);
  const setIsSearching = usePublicationFilterStore((state) => state.setIsSearching);

  const [rows, setRows] = useState<PublicationRow[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [scienceDrivers, setScienceDrivers] = useState<ScienceDriverDto[]>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>(loadStoredSortModel());
  const [editingPublication, setEditingPublication] = useState<PublicationDto | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
    loadStoredPublicationColumnVisibility()
  );

  useEffect(() => {
    void backend.publication
      .getScienceDrivers()
      .then((items) => setScienceDrivers(items ?? []))
      .catch((error) => log.error('[PublicationGrid] Failed to load science drivers', error));
  }, [backend.publication]);

  const triggerReload = useCallback(() => {
    setReloadToken((v) => v + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const userInitiatedSearch = consumeNextSearchUserInitiated();
      try {
        setLoading(true);
        setIsSearching(true);
        const start = paginationModel.page * paginationModel.pageSize;
        const length = paginationModel.pageSize;
        const draw = paginationModel.page + 1;
        const sortColumn = sortModel[0]?.field || 'year';
        const sortOrder = sortModel[0]?.sort === 'desc' ? 'Desc' : 'Asc';

        const result = await backend.publication.search(sortColumn, sortOrder, start, length, draw, filters);
        if (cancelled) return;
        const data = (result?.data || []) as PublicationDto[];
        setRows(data.filter((r) => r.id !== undefined && r.id !== null).map((r) => ({ ...r, id: r.id })));
        setTotalRows(result?.recordsFiltered ?? 0);
      } catch (error) {
        if (!cancelled) {
          log.error('[PublicationGrid] Failed to search publications', error);
          handleGridSearchError(error, { userInitiatedSearch });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setIsSearching(false);
        }
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [
    backend.publication,
    filters,
    paginationModel.page,
    paginationModel.pageSize,
    refreshNonce,
    reloadToken,
    setIsSearching,
    handleGridSearchError,
    sortModel,
  ]);

  const scienceDriverMap = useMemo(() => {
    const map = new Map<number, ScienceDriverDto>();
    scienceDrivers.forEach((sd) => {
      if (sd.id !== undefined) map.set(sd.id, sd);
    });
    return map;
  }, [scienceDrivers]);

  const onDelete = useCallback(
    async (row: PublicationRow) => {
      const confirmed = await showConfirmDialog({
        title: 'Remove publication',
        message: 'Do you want to remove this publication from the publication list? It will be hidden, not deleted.',
        confirmText: 'Remove',
        isDestructive: true,
      });
      if (!confirmed) return;
      try {
        await backend.publication.delete(row.doi || String(row.id));
        showSnackbar('Publication removed.', 'success');
        triggerReload();
      } catch (error) {
        log.error('[PublicationGrid] Failed to remove publication', error);
        showSnackbar('Failed to remove publication.', 'error');
      }
    },
    [backend.publication, showConfirmDialog, showSnackbar, triggerReload]
  );

  const columns: GridColDef[] = [
    { ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF, width: 44, minWidth: 44, maxWidth: 44 },
    {
      field: 'authors',
      headerName: 'Authors',
      flex: 1.6,
      minWidth: 240,
      renderCell: (params) => (
        <span dangerouslySetInnerHTML={{ __html: removeLeadingTrailingBrTags(String(params.value || '')) }} />
      ),
    },
    {
      field: 'title',
      headerName: 'Title',
      flex: 1.3,
      minWidth: 220,
      renderCell: (params) => (
        <span dangerouslySetInnerHTML={{ __html: removeLeadingTrailingBrTags(String(params.value || '')) }} />
      ),
    },
    {
      field: 'journal',
      headerName: 'Journal',
      flex: 1,
      minWidth: 170,
      renderCell: (params) => {
        const row = params.row as PublicationRow;
        const journalHtml = removeLeadingTrailingBrTags(String(params.value || ''));
        if (!row.doi) return <>{params.value}</>;
        return (
          <a href={toDoiHref(row.doi)} target="_blank" rel="noopener noreferrer">
            <span dangerouslySetInnerHTML={{ __html: journalHtml }} />
          </a>
        );
      },
    },
    { field: 'year', headerName: 'Year', width: 90, minWidth: 90 },
    {
      field: 'materials',
      headerName: 'Materials',
      flex: 0.9,
      minWidth: 130,
      sortable: false,
      renderCell: (params) => {
        const mats = (params.row as PublicationRow).materials || [];
        return (
          <>
            {mats.length
              ? mats
                  .map((m) => m.name)
                  .filter(Boolean)
                  .join(', ')
              : 'non-specific'}
          </>
        );
      },
    },
    {
      field: 'projectType',
      headerName: 'Project Type',
      flex: 0.95,
      minWidth: 160,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as PublicationRow;
        const projects = row.projects || [];
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, py: 0.5 }}>
            <span>{getPublicationTypeDisplay(row.type)}</span>
            {projects.map((p) => (
              <span key={p.id}>{p.projectLabel || p.id}</span>
            ))}
          </Box>
        );
      },
    },
    {
      field: 'scienceDriver',
      headerName: 'Science Driver',
      flex: 0.8,
      minWidth: 130,
      sortable: true,
      renderCell: (params) => {
        const row = params.row as PublicationRow;
        const drivers = (row.scienceDriverIds || [])
          .map((id) => scienceDriverMap.get(id))
          .filter((d): d is ScienceDriverDto => Boolean(d));
        if (!drivers.length) return <>N/A</>;
        const shortText = drivers
          .map((d) => d.name || d.label)
          .filter(Boolean)
          .join(', ');
        const longText = drivers
          .map((d) => d.label || d.name)
          .filter(Boolean)
          .join(', ');
        return (
          <Tooltip title={longText}>
            <span>{shortText}</span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <>
      <DataGridPro
        rows={rows}
        columns={columns}
        loading={loading}
        rowCount={totalRows}
        pagination
        paginationMode="server"
        sortingMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        sortModel={sortModel}
        onSortModelChange={(model) => {
          setSortModel(model);
          scopedLocalStorage.setItem(PUBLICATION_SORT_MODEL_STORAGE_KEY, JSON.stringify(model));
        }}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(model) => {
          setColumnVisibilityModel(model);
          scopedLocalStorage.setItem(COLUMN_VISIBILITY_STORAGE_KEY, JSON.stringify(model));
        }}
        pageSizeOptions={[25, 50, 100]}
        disableRowSelectionOnClick
        getRowHeight={() => 'auto'}
        initialState={{ pinnedColumns: { left: [GRID_DETAIL_PANEL_TOGGLE_FIELD, 'authors'] } }}
        getDetailPanelHeight={() => 'auto'}
        getDetailPanelContent={({ row }) => {
          const pub = row as PublicationRow;
          const description = removeLeadingTrailingBrTags(pub.description || '');
          const citation = removeLeadingTrailingBrTags(pub.cite || '');
          const instruments = [...(pub.synthesisInstruments || []), ...(pub.characterizationInstruments || [])];
          const hasAnyDetail = Boolean(
            description || citation || pub.doi || instruments.length || canEdit || canDelete
          );
          if (!hasAnyDetail) return null;

          return (
            <Box
              sx={{
                bgcolor: '#f9f9f9',
                border: '1px solid #ccc',
                borderRadius: 2,
                boxShadow: 2,
                m: 2,
                p: 2.5,
              }}
            >
              {canEdit || canDelete ? (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
                  {canDelete ? (
                    <Button size="small" variant="contained" color="error" onClick={() => void onDelete(pub)}>
                      Delete
                    </Button>
                  ) : null}
                  {canEdit ? (
                    <Button size="small" variant="contained" onClick={() => setEditingPublication(pub)}>
                      Edit
                    </Button>
                  ) : null}
                </Box>
              ) : null}

              {description ? (
                <>
                  <Box sx={{ fontWeight: 700, mb: 0.75 }}>Description:</Box>
                  <Box sx={{ mb: 2, pl: 0.5 }} dangerouslySetInnerHTML={{ __html: description }} />
                </>
              ) : null}
              {citation ? (
                <>
                  <Box sx={{ fontWeight: 700, mb: 0.75 }}>Citation:</Box>
                  <Box sx={{ mb: 2, pl: 0.5 }} dangerouslySetInnerHTML={{ __html: citation }} />
                </>
              ) : null}
              {pub.doi ? (
                <>
                  <Box sx={{ fontWeight: 700, mb: 0.75 }}>DOI:</Box>
                  <Box sx={{ mb: 2, pl: 0.5 }}>
                    <a href={toDoiHref(pub.doi)} target="_blank" rel="noopener noreferrer">
                      {stripDoiPrefix(pub.doi)}
                    </a>
                  </Box>
                </>
              ) : null}
              {instruments.length ? (
                <>
                  <Box sx={{ fontWeight: 700, mb: 0.75 }}>Instruments used:</Box>
                  <ul style={{ marginTop: 0, marginBottom: 0, paddingLeft: '1.25rem' }}>
                    {instruments.map((i) => (
                      <li key={`${i.id}-${i.name}`}>
                        {i.id && i.name && i.id !== i.name
                          ? `${i.id} (${i.name})`
                          : i.name || i.id || 'Unknown instrument'}{' '}
                        {i.doi ? (
                          <>
                            - DOI:{' '}
                            <a href={toDoiHref(i.doi)} target="_blank" rel="noopener noreferrer">
                              {stripDoiPrefix(i.doi)}
                            </a>
                          </>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
            </Box>
          );
        }}
        sx={dataGridStyle}
      />
      <CreatePublicationDialog
        key={editingPublication ? `edit-${editingPublication.id}` : 'edit-none'}
        open={Boolean(editingPublication)}
        publication={editingPublication ?? undefined}
        onClose={() => {
          setEditingPublication(null);
          triggerReload();
        }}
      />
    </>
  );
}
